import { useState, useRef, useCallback, useEffect } from 'react';

export interface MediaDeviceOption {
  deviceId: string;
  label: string;
}

interface UseMediaRecorderReturn {
  stream: MediaStream | null;
  isRecording: boolean;
  isPreviewing: boolean;
  duration: number;
  error: string | null;
  videoDevices: MediaDeviceOption[];
  audioDevices: MediaDeviceOption[];
  selectedVideoDevice: string;
  selectedAudioDevice: string;
  setSelectedVideoDevice: (id: string) => void;
  setSelectedAudioDevice: (id: string) => void;
  deviceStatus: DeviceStatus;
  enumerateDevices: () => Promise<void>;
  startCamera: (videoDeviceId?: string, audioDeviceId?: string) => Promise<boolean>;
  startScreen: (opts?: { withWebcam?: boolean }) => Promise<void>;
  startRecording: (recordingStream?: MediaStream) => void;
  stopRecording: () => Promise<Blob>;
  cancelRecording: () => void;
  stopCamera: () => void;
}

const isMobile = () => /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

// 'requesting' is the only state that should show a spinner. Before this, an empty
// device list was indistinguishable from a failed one, so a blocked camera sat on
// "Detecting cameras..." forever.
export type DeviceStatus = 'idle' | 'requesting' | 'ready' | 'error';

// getUserMedia error names are not something to show a person. NotReadableError in
// particular is the common Windows case — Zoom or Teams already holds the camera —
// and the browser's own wording for it is "Could not start video source".
function friendlyMediaError(err: any): string {
  const name = err?.name || '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Your browser is blocking camera access. Click the camera icon in the address bar, choose Allow, then try again.';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'No camera found. Plug one in, or check that it is switched on, then try again.';
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return 'Another app is already using your camera. Close Zoom, Teams, FaceTime or any other video app, then try again.';
  }
  if (name === 'OverconstrainedError' || name === 'ConstraintNotSatisfiedError') {
    return 'That camera would not start at the requested quality. Try a different camera from the list.';
  }
  if (name === 'AbortError') {
    return 'The camera stopped responding. Try again, and restart your browser if it keeps happening.';
  }
  return err?.message ? `Camera error: ${err.message}` : 'Could not start the camera. Try again.';
}

export function useMediaRecorder(): UseMediaRecorderReturn {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [deviceStatus, setDeviceStatus] = useState<DeviceStatus>('idle');
  const [videoDevices, setVideoDevices] = useState<MediaDeviceOption[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceOption[]>([]);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState('');
  const [selectedAudioDevice, setSelectedAudioDevice] = useState('');

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const resolveStopRef = useRef<((blob: Blob) => void) | null>(null);

  // Canvas recording refs (mobile only — fixes rotation/orientation metadata issues)
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const canvasVideoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRafRef = useRef<number | null>(null);

  const stopAllTracks = useCallback((s: MediaStream | null) => {
    s?.getTracks().forEach(t => t.stop());
  }, []);

  const enumerateDevices = useCallback(async () => {
    setDeviceStatus('requesting');
    setError(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setDeviceStatus('error');
        setError('This browser cannot record video. Use Chrome, Edge, Firefox or Safari, and make sure the page is on https.');
        return;
      }
      // Labels stay hidden until permission is granted, so a throwaway stream comes
      // first. Asking for both at once means a missing microphone also wipes out the
      // camera list, so fall back to each on its own.
      let tempStream: MediaStream | null = null;
      try {
        tempStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      } catch (bothErr: any) {
        if (bothErr?.name === 'NotAllowedError' || bothErr?.name === 'SecurityError') throw bothErr;
        const partial: MediaStreamTrack[] = [];
        for (const c of [{ video: true }, { audio: true }] as MediaStreamConstraints[]) {
          try {
            const one = await navigator.mediaDevices.getUserMedia(c);
            partial.push(...one.getTracks());
          } catch { /* that kind simply isn't available */ }
        }
        if (!partial.length) throw bothErr;
        tempStream = new MediaStream(partial);
      }
      const devices = await navigator.mediaDevices.enumerateDevices();
      tempStream.getTracks().forEach(t => t.stop());

      const video = devices
        .filter(d => d.kind === 'videoinput')
        .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Camera ${i + 1}` }));
      const audio = devices
        .filter(d => d.kind === 'audioinput')
        .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Microphone ${i + 1}` }));

      setVideoDevices(video);
      setAudioDevices(audio);

      // Auto-select first device if none selected
      if (!selectedVideoDevice && video.length > 0) setSelectedVideoDevice(video[0].deviceId);
      if (!selectedAudioDevice && audio.length > 0) setSelectedAudioDevice(audio[0].deviceId);

      if (!video.length) {
        setDeviceStatus('error');
        setError('No camera found. Plug one in, or check that it is switched on, then try again.');
      } else {
        setDeviceStatus('ready');
      }
    } catch (err: any) {
      setDeviceStatus('error');
      setError(friendlyMediaError(err));
    }
  }, [selectedVideoDevice, selectedAudioDevice]);

  const startCamera = useCallback(async (videoDeviceId?: string, audioDeviceId?: string) => {
    setError(null);
    const vidId = videoDeviceId || selectedVideoDevice;
    const audId = audioDeviceId || selectedAudioDevice;

    try {
      // On mobile: don't constrain width/height — let the camera use its natural
      // orientation. Dimension constraints can force landscape on portrait phones.
      const videoConstraint: MediaTrackConstraints = vidId
        ? { deviceId: { exact: vidId } }
        : { facingMode: 'user' };

      if (isMobile()) {
        // Cap any edge at 1920 so 4K phone cameras don't capture giant files
        // (a 4K, 2-min clip makes downstream matting/render crawl). max on both
        // axes — not ideal-only — because `ideal` is a soft hint cameras ignore.
        // No orientation forcing: the canvas step bakes portrait/landscape.
        videoConstraint.width = { ideal: 1920, max: 1920 };
        videoConstraint.height = { ideal: 1920, max: 1920 };
      } else {
        // Desktop self-tapes: target 1080p 16:9 and HARD-cap at 1920x1080 so a
        // 4:3 4K webcam can't hand back a 4K 4:3 file. `max` is the real fix —
        // `ideal`/`aspectRatio` alone are hints these cameras override. The
        // canvas step below corrects aspect if the camera still gives 4:3.
        videoConstraint.width = { ideal: 1920, max: 1920 };
        videoConstraint.height = { ideal: 1080, max: 1080 };
        videoConstraint.aspectRatio = { ideal: 16 / 9 };
      }

      const constraints: MediaStreamConstraints = {
        video: videoConstraint,
        audio: audId ? { deviceId: { exact: audId } } : true,
      };
      let s: MediaStream;
      try {
        s = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (firstErr: any) {
        // A remembered deviceId that has since been unplugged, or a camera that
        // cannot do 1080p, fails on the exact constraint. Retry with defaults
        // before telling anyone it is broken.
        const worthRelaxing = ['OverconstrainedError', 'ConstraintNotSatisfiedError', 'NotFoundError'].includes(firstErr?.name);
        if (!worthRelaxing) throw firstErr;
        s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      }
      setStream(s);
      setIsPreviewing(true);
      return true;
    } catch (err: any) {
      setError(friendlyMediaError(err));
      setIsPreviewing(false);
      return false;
    }
  }, [selectedVideoDevice, selectedAudioDevice]);

  const cleanupCanvas = useCallback(() => {
    if (canvasRafRef.current) {
      cancelAnimationFrame(canvasRafRef.current);
      canvasRafRef.current = null;
    }
    if (canvasVideoRef.current) {
      canvasVideoRef.current.srcObject = null;
      canvasVideoRef.current = null;
    }
    canvasRef.current = null;
  }, []);

  // Screen-recording state (screen + optional webcam PiP, composited on a canvas).
  const screenRef = useRef<{ screenStream?: MediaStream; camStream?: MediaStream; audioCtx?: AudioContext; raf?: number } | null>(null);

  const cleanupScreen = useCallback(() => {
    const s = screenRef.current;
    if (!s) return;
    if (s.raf) cancelAnimationFrame(s.raf);
    s.screenStream?.getTracks().forEach(t => t.stop());
    s.camStream?.getTracks().forEach(t => t.stop());
    try { s.audioCtx?.close(); } catch { /* ignore */ }
    screenRef.current = null;
  }, []);

  const startRecording = useCallback((recordingStream?: MediaStream) => {
    const recStream = recordingStream || stream;
    if (!recStream) return;
    setError(null);
    chunksRef.current = [];

    let streamToRecord = recStream;

    // Determine if we need canvas recording:
    // - Mobile: always (bakes orientation into pixels, avoids rotation metadata issues)
    // - Desktop: only if the camera gave a bad aspect ratio (e.g. square)
    const videoTrack = recStream.getVideoTracks()[0];
    const settings = videoTrack?.getSettings();
    const srcW = settings?.width || 1280;
    const srcH = settings?.height || 720;
    const ratio = srcW / srcH;
    // Anything narrower than ~16:9 on desktop (square OR 4:3 webcams) gets
    // center-cropped to 16:9 — previously only ~1:1 was caught, so 4:3 (1.33)
    // slipped through and recorded as-is (the "4K 4:3 oof" bug).
    const needsAspectFix = ratio < 1.70;
    const needsCanvas = isMobile() || needsAspectFix;

    if (needsCanvas) {
      try {
        let canvasW = srcW;
        let canvasH = srcH;

        if (needsAspectFix) {
          // Force to proper aspect ratio by cropping (center-crop the shorter axis)
          // Detect orientation: landscape if screen is wider than tall, portrait otherwise
          const isLandscape = !isMobile() || window.innerWidth > window.innerHeight;
          if (isLandscape) {
            // 16:9 landscape
            canvasW = srcW;
            canvasH = Math.min(srcH, Math.round(srcW * 9 / 16));
          } else {
            // 9:16 portrait
            canvasW = Math.min(srcW, Math.round(srcH * 9 / 16));
            canvasH = srcH;
          }
        }

        const video = document.createElement('video');
        video.srcObject = recStream;
        video.muted = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.play();
        canvasVideoRef.current = video;

        const canvas = document.createElement('canvas');
        canvas.width = canvasW;
        canvas.height = canvasH;
        canvasRef.current = canvas;
        const ctx = canvas.getContext('2d')!;

        // Center-crop: offset into the source to extract the right region
        const sx = Math.round((srcW - canvasW) / 2);
        const sy = Math.round((srcH - canvasH) / 2);

        const draw = () => {
          ctx.drawImage(video, sx, sy, canvasW, canvasH, 0, 0, canvasW, canvasH);
          canvasRafRef.current = requestAnimationFrame(draw);
        };
        draw();

        const canvasStream = canvas.captureStream(30);
        // Add audio tracks from original stream
        recStream.getAudioTracks().forEach(t => canvasStream.addTrack(t));
        streamToRecord = canvasStream;
      } catch (e) {
        console.warn('Canvas recording setup failed, falling back to direct recording:', e);
        // Fall through to direct recording
      }
    }

    // Prefer mp4 — Chrome 126+ (June 2024) and Safari produce H.264/AAC mp4
    // directly from MediaRecorder, skipping the server-side MediaConvert
    // round-trip. Firefox doesn't support mp4 MediaRecorder yet and falls
    // back to webm; SelfTapeEnhancer's convertMovClip handles that case.
    const mimeTypes = [
      'video/mp4;codecs=avc1.42E01F,mp4a.40.2', // H.264 baseline + AAC-LC
      'video/mp4',
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm'
    ];
    const mimeType = mimeTypes.find(t => MediaRecorder.isTypeSupported(t)) || '';

    const recorder = new MediaRecorder(streamToRecord, {
      mimeType: mimeType || undefined,
      videoBitsPerSecond: 8_000_000, // 8 Mbps — much sharper than browser default (~2.5 Mbps)
    });
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'video/webm' });
      cleanupCanvas();
      if (resolveStopRef.current) {
        resolveStopRef.current(blob);
        resolveStopRef.current = null;
      }
    };

    recorder.start(1000);
    setIsRecording(true);
    setDuration(0);

    timerRef.current = window.setInterval(() => {
      setDuration(d => d + 1);
    }, 1000);
  }, [stream, cleanupCanvas]);

  const stopRecording = useCallback((): Promise<Blob> => {
    return new Promise((resolve) => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setIsRecording(false);

      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        resolveStopRef.current = resolve;
        recorderRef.current.stop();
      } else {
        cleanupCanvas();
        resolve(new Blob(chunksRef.current, { type: 'video/webm' }));
      }
    });
  }, [cleanupCanvas]);

  const cancelRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    resolveStopRef.current = null;
    chunksRef.current = [];
    cleanupCanvas();
    setIsRecording(false);
    setDuration(0);
  }, [cleanupCanvas]);

  const stopCamera = useCallback(() => {
    cancelRecording();
    cleanupScreen();
    stopAllTracks(stream);
    setStream(null);
    setIsPreviewing(false);
  }, [stream, cancelRecording, stopAllTracks, cleanupScreen]);

  // Screen + optional webcam bubble. getDisplayMedia for the screen, getUserMedia
  // for the webcam, composited on a canvas (screen base + rounded PiP bottom-right).
  // Mic + system audio are mixed into one track via Web Audio. The composited
  // canvas stream becomes `stream`, so startRecording records it directly (it's a
  // clean 16:9, so the aspect-fix path is skipped). Desktop only — getDisplayMedia
  // isn't available on mobile browsers.
  const startScreen = useCallback(async (opts?: { withWebcam?: boolean }) => {
    setError(null);
    const withWebcam = opts?.withWebcam !== false;
    try {
      const screenStream = await (navigator.mediaDevices as MediaDevices).getDisplayMedia({
        video: { frameRate: 30 } as MediaTrackConstraints,
        audio: true,
      });

      let camStream: MediaStream | undefined;
      if (withWebcam) {
        try {
          camStream = await navigator.mediaDevices.getUserMedia({
            video: { width: { ideal: 640 }, height: { ideal: 480 } },
            audio: selectedAudioDevice ? { deviceId: { exact: selectedAudioDevice } } : true,
          });
        } catch { camStream = undefined; /* webcam optional — continue screen-only */ }
      }

      const screenVideo = document.createElement('video');
      screenVideo.srcObject = screenStream; screenVideo.muted = true; screenVideo.playsInline = true;
      await screenVideo.play().catch(() => {});
      let camVideo: HTMLVideoElement | undefined;
      if (camStream) {
        camVideo = document.createElement('video');
        camVideo.srcObject = camStream; camVideo.muted = true; camVideo.playsInline = true;
        await camVideo.play().catch(() => {});
      }

      const canvasW = 1280, canvasH = 720;
      const canvas = document.createElement('canvas');
      canvas.width = canvasW; canvas.height = canvasH;
      const ctx = canvas.getContext('2d')!;

      const roundRect = (x: number, y: number, w: number, h: number, r: number) => {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r);
        ctx.closePath();
      };
      const drawCover = (v: HTMLVideoElement, x: number, y: number, w: number, h: number) => {
        const vw = v.videoWidth || 4, vh = v.videoHeight || 3;
        const scale = Math.max(w / vw, h / vh);
        const dw = vw * scale, dh = vh * scale;
        ctx.drawImage(v, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
      };

      const draw = () => {
        drawCover(screenVideo, 0, 0, canvasW, canvasH);
        if (camVideo) {
          const bw = 260, bh = 195, bx = canvasW - bw - 24, by = canvasH - bh - 24;
          ctx.save(); roundRect(bx, by, bw, bh, 16); ctx.clip(); drawCover(camVideo, bx, by, bw, bh); ctx.restore();
          ctx.save(); roundRect(bx, by, bw, bh, 16); ctx.lineWidth = 4; ctx.strokeStyle = 'rgba(255,255,255,0.92)'; ctx.stroke(); ctx.restore();
        }
        const raf = requestAnimationFrame(draw);
        if (screenRef.current) screenRef.current.raf = raf;
      };
      draw();

      const canvasStream = canvas.captureStream(30);

      // Mix system + mic audio into one track.
      let audioCtx: AudioContext | undefined;
      const audioSources = [screenStream, camStream].filter(s => s && s.getAudioTracks().length) as MediaStream[];
      if (audioSources.length) {
        audioCtx = new AudioContext();
        const dest = audioCtx.createMediaStreamDestination();
        audioSources.forEach(s => audioCtx!.createMediaStreamSource(new MediaStream(s.getAudioTracks())).connect(dest));
        dest.stream.getAudioTracks().forEach(t => canvasStream.addTrack(t));
      }

      screenRef.current = { screenStream, camStream, audioCtx };

      // User ends the share from the browser chrome → tear everything down.
      screenStream.getVideoTracks()[0]?.addEventListener('ended', () => { stopCamera(); });

      setStream(canvasStream);
      setIsPreviewing(true);
    } catch (err: any) {
      if (err.name === 'NotAllowedError') setError('Screen share was cancelled or denied.');
      else setError(`Screen capture error: ${err.message}`);
      cleanupScreen();
      throw err;
    }
  }, [selectedAudioDevice, stopCamera, cleanupScreen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      cleanupCanvas();
      cleanupScreen();
      stopAllTracks(stream);
    };
  }, [stream, stopAllTracks, cleanupCanvas, cleanupScreen]);

  return {
    stream,
    isRecording,
    isPreviewing,
    duration,
    error,
    videoDevices,
    audioDevices,
    selectedVideoDevice,
    selectedAudioDevice,
    setSelectedVideoDevice,
    setSelectedAudioDevice,
    deviceStatus,
    enumerateDevices,
    startCamera,
    startScreen,
    startRecording,
    stopRecording,
    cancelRecording,
    stopCamera,
  };
}
